import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOppBotContext, useOppBotQuery } from "../hooks/useOppBotApi";
import { Avatar, ConfidenceBar, EmptyState, ErrorBox, ExternalLink, IntentPill, ModePill, RecordTypePill, Spinner, StatusPill } from "../components/Ui";
import EmlUploader from "../components/EmlUploader";
import { formatDateTime, formatMs, isProcessing, relativeTime } from "../utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "Needs review", countKey: "needs_review" },
  { key: "received,parsing,interpreting,applying", label: "Processing", countKeys: ["received", "parsing", "interpreting", "applying"] },
  { key: "applied", label: "Applied", countKey: "applied" },
  { key: "not_sales", label: "Not a deal", countKey: "not_sales" },
  { key: "failed,rejected", label: "Failed / rejected", countKeys: ["failed", "rejected"] },
];

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function InboxPage() {
  const navigate = useNavigate();
  const { meta } = useOppBotContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "all";
  const forwarder = searchParams.get("forwarder") || "";
  const page = Number(searchParams.get("page") || 1);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const dq = useDebounced(q, 300);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (dq) next.set("q", dq);
    else next.delete("q");
    if ((searchParams.get("q") || "") !== dq) {
      next.delete("page");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const listParams = useMemo(() => ({ status, q: dq, forwarder, page, limit: 50 }), [status, dq, forwarder, page]);
  const list = useOppBotQuery("emails", listParams, { pollMs: 15000 });
  const statsParams = useMemo(() => ({}), []);
  const stats = useOppBotQuery("stats", statsParams, { pollMs: 30000 });
  const counts = (stats.data && stats.data.counts) || {};
  const items = (list.data && list.data.items) || [];
  const total = list.data ? list.data.total : 0;
  const pages = Math.max(1, Math.ceil(total / 50));
  const threshold = meta ? meta.autoApplyMinConfidence : undefined;

  return (
    <>
      <div className="ob-page-head">
        <div>
          <h1>Inbox</h1>
          <div className="ob-page-head__sub">
            Every email forwarded to the bot, what it understood, and what it did in Salesforce.
            {stats.data && stats.data.avgProcessingMs ? ` Average processing time ${formatMs(stats.data.avgProcessingMs)}.` : ""}
          </div>
        </div>
        <div className="ob-page-head__actions">
          <EmlUploader compact />
          <button type="button" className="ob-btn ob-btn--sm" onClick={() => { list.refetch(); stats.refetch(); }}>
            Refresh
          </button>
        </div>
      </div>

      <div className="ob-stats">
        <Stat label="Last 7 days" value={stats.data ? stats.data.last7Days : "—"} hint={stats.data ? `${stats.data.last30Days} in 30 days · ${stats.data.total} all time` : ""} />
        <Stat label="Needs review" value={counts.needs_review ?? "—"} tone="warning" hint="Waiting for a human" active={status === "needs_review"} onClick={() => setParam("status", status === "needs_review" ? "all" : "needs_review")} />
        <Stat label="Applied" value={counts.applied ?? "—"} tone="success" hint="Written to Salesforce" active={status === "applied"} onClick={() => setParam("status", status === "applied" ? "all" : "applied")} />
        <Stat label="Not a deal" value={counts.not_sales ?? "—"} hint="Logged, no action" active={status === "not_sales"} onClick={() => setParam("status", status === "not_sales" ? "all" : "not_sales")} />
        <Stat label="Failed / rejected" value={(counts.failed || 0) + (counts.rejected || 0)} tone={(counts.failed || 0) > 0 ? "danger" : undefined} hint={`${counts.failed || 0} failed · ${counts.rejected || 0} rejected`} active={status === "failed,rejected"} onClick={() => setParam("status", status === "failed,rejected" ? "all" : "failed,rejected")} />
        <Stat label="Avg confidence" value={stats.data && stats.data.avgConfidence !== null && stats.data.avgConfidence !== undefined ? `${Math.round(stats.data.avgConfidence * 100)}%` : "—"} hint={threshold !== undefined ? `Auto-apply at ${Math.round(threshold * 100)}%` : ""} />
      </div>

      <div className="ob-toolbar">
        <div className="ob-toolbar__left">
          <div className="ob-chips" role="tablist">
            {FILTERS.map((f) => {
              const count = f.countKey ? counts[f.countKey] : f.countKeys ? f.countKeys.reduce((a, k) => a + (counts[k] || 0), 0) : null;
              return (
                <button key={f.key} type="button" role="tab" className={`ob-chip ${status === f.key ? "is-active" : ""}`} onClick={() => setParam("status", f.key)}>
                  {f.label}
                  {count !== null && count !== undefined && <span className="ob-chip__count">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="ob-toolbar__right">
          {stats.data && stats.data.forwarders && stats.data.forwarders.length > 0 && (
            <select className="ob-select" value={forwarder} onChange={(e) => setParam("forwarder", e.target.value)} aria-label="Filter by forwarder">
              <option value="">Everyone</option>
              {stats.data.forwarders.map((f) => (
                <option key={f.email} value={f.email}>
                  {f.name || f.email} ({f.count})
                </option>
              ))}
            </select>
          )}
          <div className="ob-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, company, forwarder…" aria-label="Search" />
          </div>
        </div>
      </div>

      <div className="ob-table-wrap">
        {list.error ? (
          <ErrorBox error={list.error} onRetry={list.reload} />
        ) : list.loading && !list.data ? (
          <Spinner label="Loading emails…" />
        ) : items.length === 0 ? (
          <EmptyState
            title={status === "all" && !dq && !forwarder ? "Nothing forwarded yet" : "No emails match"}
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 4h16v12H7l-3 3z" />
                <path d="M9 9h6M9 12h4" />
              </svg>
            }
          >
            {status === "all" && !dq && !forwarder ? (
              <>
                Forward any sales email thread to <code>{meta ? meta.inboundAddress : "the bot address"}</code> from your work mailbox. Add notes at the top like <code>For SIS 2027</code> or <code>Ethan to lead</code> to steer it.
              </>
            ) : (
              "Try a different filter or search term."
            )}
          </EmptyState>
        ) : (
          <table className="ob-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Forwarded by</th>
                <th>Email</th>
                <th>AI read</th>
                <th>Confidence</th>
                <th>Salesforce</th>
                <th className="ob-td-right">Received</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Row key={item._id} item={item} threshold={threshold} onOpen={() => navigate(`/landmark/oppbot/email/${item._id}`)} />
              ))}
            </tbody>
          </table>
        )}
        {pages > 1 && (
          <div className="ob-pager">
            <span>
              Page {page} of {pages} · {total} emails
            </span>
            <div className="ob-pager__btns">
              <button type="button" className="ob-btn ob-btn--sm" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))}>
                Previous
              </button>
              <button type="button" className="ob-btn ob-btn--sm" disabled={page >= pages} onClick={() => setParam("page", String(page + 1))}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, hint, tone, active, onClick }) {
  return (
    <div className={`ob-stat ${tone ? `ob-stat--${tone}` : ""} ${onClick ? "is-clickable" : ""} ${active ? "is-active" : ""}`} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}>
      <div className="ob-stat__label">{label}</div>
      <div className="ob-stat__value">{value}</div>
      {hint && <div className="ob-stat__hint">{hint}</div>}
    </div>
  );
}

function Row({ item, threshold, onOpen }) {
  const ai = item.ai || {};
  const result = item.result || {};
  const company = (ai.prospect && ai.prospect.companyName) || "";
  const processing = isProcessing(item.status);
  return (
    <tr className="is-clickable" onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      <td>
        <StatusPill status={item.status} />
      </td>
      <td>
        <div className="ob-td-forwarder">
          <Avatar name={item.forwarder && item.forwarder.name} email={item.forwarder && item.forwarder.email} />
          <div>
            <div className="ob-td-forwarder__name">{(item.forwarder && item.forwarder.name) || "Unknown"}</div>
            <div className="ob-td-forwarder__email">{item.forwarder && item.forwarder.email}</div>
          </div>
        </div>
      </td>
      <td className="ob-td-subject">
        <div className="ob-td-subject__main ob-ellipsis" title={item.email && item.email.subject}>
          {(item.email && item.email.subject) || "(no subject)"}
        </div>
        <div className="ob-td-subject__sub ob-ellipsis" title={item.parsed && item.parsed.notes}>
          {item.parsed && item.parsed.notesBullets && item.parsed.notesBullets.length
            ? item.parsed.notesBullets.join(" · ")
            : item.parsed && item.parsed.notes
            ? item.parsed.notes.replace(/\s+/g, " ").slice(0, 120)
            : processing
            ? "Processing…"
            : ""}
        </div>
      </td>
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <IntentPill doc={item} />
            {ai.opportunity && ai.opportunity.recordTypeName && ["new_opportunity", "update_opportunity"].includes(ai.intent) && (
              <RecordTypePill name={ai.opportunity.recordTypeName} curateCategory={ai.opportunity.curateCategory} />
            )}
            <ModePill mode={item.parsed && item.parsed.mode} />
          </div>
          {company && <span className="ob-muted" style={{ fontSize: 12.5 }}>{company}</span>}
        </div>
      </td>
      <td>
        <ConfidenceBar value={ai.confidence} threshold={threshold} />
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {result.opportunity && result.opportunity.sfId ? (
          <ExternalLink href={result.opportunity.url}>
            <span className="ob-ellipsis" style={{ maxWidth: 220 }} title={result.opportunity.name}>
              {result.opportunity.name || "Opportunity"}
            </span>
          </ExternalLink>
        ) : item.status === "needs_review" ? (
          <span className="ob-muted">Awaiting approval</span>
        ) : item.status === "failed" && item.error && item.error.message ? (
          <span className="ob-inline-error ob-ellipsis" style={{ maxWidth: 220 }} title={item.error.message}>
            {item.error.message}
          </span>
        ) : (
          <span className="ob-muted">—</span>
        )}
      </td>
      <td className="ob-td-right ob-td-time" title={formatDateTime(item.receivedAt)}>
        {relativeTime(item.receivedAt)}
      </td>
    </tr>
  );
}
